import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Alert } from '../components/common/Alert';
import { ProgressBar } from '../components/common/ProgressBar';
import { Select } from '../components/common/Select';
import api from '../services/api';

interface SupportedLanguages {
  [key: string]: string;
}

interface TranslationResponse {
  success: boolean;
  translatedText?: string;
  error?: string;
  metadata?: {
    originalLength: number;
    translatedLength: number;
    targetLanguage: string;
    sourceLanguage: string;
    translatedAt: string;
  };
}

const DocumentTranslation: React.FC = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [targetLanguage, setTargetLanguage] = useState<string>('');
  const [sourceLanguage, setSourceLanguage] = useState<string>('auto');
  const [context, setContext] = useState<string>('');
  const [isTranslating, setIsTranslating] = useState<boolean>(false);
  const [translationProgress, setTranslationProgress] = useState<number>(0);
  const [supportedLanguages, setSupportedLanguages] = useState<SupportedLanguages>({});
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [translationMode, setTranslationMode] = useState<'document' | 'text'>('document');
  const [textToTranslate, setTextToTranslate] = useState<string>('');
  const [translatedText, setTranslatedText] = useState<string>('');
  const [outputFormat, setOutputFormat] = useState<'pdf' | 'image'>('pdf');

  // Load supported languages on component mount
  React.useEffect(() => {
    loadSupportedLanguages();
  }, []);

  const loadSupportedLanguages = async () => {
    try {
      const response = await api.get('/translate/languages');
      if (response.data.success) {
        setSupportedLanguages(response.data.supportedLanguages);
      }
    } catch (error) {
      console.error('Failed to load supported languages:', error);
      setAlert({
        type: 'error',
        message: 'Nie udało się załadować listy dostępnych języków'
      });
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setAlert({
          type: 'error',
          message: 'Obsługiwane są tylko pliki PDF i obrazy (JPG, JPEG, PNG)'
        });
        return;
      }

      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setAlert({
          type: 'error',
          message: 'Plik jest za duży. Maksymalny rozmiar to 10MB'
        });
        return;
      }

      setSelectedFile(file);
      setAlert(null);
    }
  };

  const handleDocumentTranslation = async () => {
    if (!selectedFile || !targetLanguage) {
      setAlert({
        type: 'error',
        message: 'Wybierz plik (PDF lub obraz) i język docelowy'
      });
      return;
    }

    setIsTranslating(true);
    setTranslationProgress(0);
    setAlert(null);

    try {
      const formData = new FormData();
      formData.append('document', selectedFile);
      formData.append('targetLanguage', targetLanguage);
      formData.append('sourceLanguage', sourceLanguage);
      formData.append('context', context);
      formData.append('outputFormat', outputFormat);

      const progressInterval = setInterval(() => {
        setTranslationProgress(prev => Math.min(prev + 5, 85));
      }, 800);

      const isLarge = selectedFile.size > 3 * 1024 * 1024 || selectedFile.type === 'application/pdf';
      let blob: Blob | null = null;
      
      try {
        if (isLarge) {
          // Use async mode to avoid timeouts on large PDFs/images
          const resp = await api.post('/translate/translate-document?async=true', formData);
          const jobId = resp.data?.jobId;
          if (!jobId) throw new Error('Brak jobId');
          // Poll for status
          const start = Date.now();
          const timeoutMs = 15 * 60 * 1000;
          while (true) {
            const st = await api.get(`/translate/status/${jobId}`);
            if (st.data?.success) {
              const prog = Number(st.data.progress || (st.data.status === 'completed' ? 100 : 0));
              setTranslationProgress(Math.min(99, Math.max(10, prog)));
              if (st.data.status === 'completed' && st.data.downloadable) {
                const dl = await api.get(`/translate/download/${jobId}`, { responseType: 'blob' as any });
                blob = dl.data as Blob;
                break;
              }
            }
            if (Date.now() - start > timeoutMs) throw new Error('Przekroczono limit czasu');
            await new Promise(r => setTimeout(r, 1500));
          }
        } else {
          const response = await api.post('/translate/translate-document', formData, {
            responseType: 'blob',
            timeout: 300000
          });
          blob = new Blob([response.data]);
        }
      } catch (err: any) {
        // Fallback to async if sync path timed out
        if (err?.code === 'ECONNABORTED' || /czas|timeout/i.test(err?.message || '')) {
          try {
            const resp = await api.post('/translate/translate-document?async=true', formData);
            const jobId = resp.data?.jobId;
            if (!jobId) throw new Error('Brak jobId');
            const start = Date.now();
            const timeoutMs = 15 * 60 * 1000;
            while (true) {
              const st = await api.get(`/translate/status/${jobId}`);
              if (st.data?.success) {
                const prog = Number(st.data.progress || (st.data.status === 'completed' ? 100 : 0));
                setTranslationProgress(Math.min(99, Math.max(10, prog)));
                if (st.data.status === 'completed' && st.data.downloadable) {
                  const dl = await api.get(`/translate/download/${jobId}`, { responseType: 'blob' as any });
                  blob = dl.data as Blob;
                  break;
                }
              }
              if (Date.now() - start > timeoutMs) throw new Error('Przekroczono limit czasu');
              await new Promise(r => setTimeout(r, 1500));
            }
          } catch (err2) {
            throw err2;
          }
        } else {
          throw err;
        }
      } finally {
        clearInterval(progressInterval);
      }

      setTranslationProgress(100);

      if (!blob) throw new Error('Brak pliku do pobrania');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const originalName = selectedFile.name.replace('.pdf', '');
      link.setAttribute('download', `${originalName}_translated_${targetLanguage}.pdf`);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setAlert({
        type: 'success',
        message: 'Dokument został przetłumaczony i pobrany pomyślnie!'
      });

    } catch (error: any) {
      console.error('Translation failed:', error);
      setAlert({
        type: 'error',
        message: error.response?.data?.error || 'Wystąpił błąd podczas tłumaczenia dokumentu'
      });
    } finally {
      setIsTranslating(false);
      setTranslationProgress(0);
    }
  };

  const handleTextTranslation = async () => {
    if (!textToTranslate.trim() || !targetLanguage) {
      setAlert({
        type: 'error',
        message: 'Wprowadź tekst do tłumaczenia i wybierz język docelowy'
      });
      return;
    }

    setIsTranslating(true);
    setTranslatedText('');
    setAlert(null);

    try {
      const response = await api.post('/translate/translate-text', {
        text: textToTranslate,
        targetLanguage,
        sourceLanguage,
        context
      });

      if (response.data.success) {
        setTranslatedText(response.data.translatedText);
        setAlert({
          type: 'success',
          message: 'Tekst został przetłumaczony pomyślnie!'
        });
      } else {
        throw new Error(response.data.error);
      }

    } catch (error: any) {
      console.error('Text translation failed:', error);
      setAlert({
        type: 'error',
        message: error.response?.data?.error || 'Wystąpił błąd podczas tłumaczenia tekstu'
      });
    } finally {
      setIsTranslating(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setTextToTranslate('');
    setTranslatedText('');
    setTargetLanguage('');
    setSourceLanguage('auto');
    setContext('');
    setOutputFormat('pdf');
    setAlert(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Tłumaczenie Dokumentów Technicznych
        </h1>
        <p className="text-gray-600">
          Tłumacz dokumenty PDF i tekst techniczny przy użyciu AI
        </p>
      </div>

      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
          className="mb-6"
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Translation Settings */}
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Ustawienia Tłumaczenia</h2>
            
            {/* Translation Mode */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tryb tłumaczenia
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="document"
                    checked={translationMode === 'document'}
                    onChange={(e) => setTranslationMode(e.target.value as 'document' | 'text')}
                    className="mr-2"
                  />
                  Dokument PDF
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="text"
                    checked={translationMode === 'text'}
                    onChange={(e) => setTranslationMode(e.target.value as 'document' | 'text')}
                    className="mr-2"
                  />
                  Tekst
                </label>
              </div>
            </div>

            {/* Language Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Język źródłowy
                </label>
                <Select
                  value={sourceLanguage}
                  onChange={(e) => setSourceLanguage(e.target.value)}
                >
                  <option value="auto">Wykryj automatycznie</option>
                  {Object.entries(supportedLanguages).map(([code, name]) => (
                    <option key={code} value={code}>{name}</option>
                  ))}
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Język docelowy *
                </label>
                <Select
                  value={targetLanguage}
                  onChange={(e) => setTargetLanguage(e.target.value)}
                  required
                >
                  <option value="">Wybierz język</option>
                  {Object.entries(supportedLanguages).map(([code, name]) => (
                    <option key={code} value={code}>{name}</option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Context */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kontekst (opcjonalny)
              </label>
              <textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="Dodaj kontekst dla lepszego tłumaczenia, np. 'dokument budowlany', 'specyfikacja techniczna'"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
              />
            </div>

            {/* Output Format (for images) */}
            {selectedFile && selectedFile.type.startsWith('image/') && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Format wyjściowy
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="pdf"
                      checked={outputFormat === 'pdf'}
                      onChange={(e) => setOutputFormat(e.target.value as 'pdf' | 'image')}
                      className="mr-2"
                    />
                    PDF z tłumaczeniem
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="image"
                      checked={outputFormat === 'image'}
                      onChange={(e) => setOutputFormat(e.target.value as 'pdf' | 'image')}
                      className="mr-2"
                    />
                    Obraz z nakładką
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Rekomendujemy PDF dla lepszej czytelności tłumaczenia
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <Button
                onClick={translationMode === 'document' ? handleDocumentTranslation : handleTextTranslation}
                disabled={isTranslating || !targetLanguage || (translationMode === 'document' && !selectedFile) || (translationMode === 'text' && !textToTranslate.trim())}
                className="flex-1"
              >
                {isTranslating ? 'Tłumaczenie...' : 'Przetłumacz'}
              </Button>
              <Button
                variant="outline"
                onClick={resetForm}
                disabled={isTranslating}
              >
                Wyczyść
              </Button>
            </div>

            {/* Progress Bar */}
            {isTranslating && translationMode === 'document' && (
              <div className="mt-4">
                <ProgressBar progress={translationProgress} />
                <p className="text-sm text-gray-600 mt-2 text-center">
                  Tłumaczenie w toku... {translationProgress}%
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Input Area */}
        <Card>
          <div className="p-6">
            {translationMode === 'document' ? (
              <div>
                <h2 className="text-lg font-semibold mb-4">Wybierz Dokument</h2>
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="document-upload"
                  />
                  <label
                    htmlFor="document-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-lg font-medium text-gray-700 mb-2">
                      Kliknij aby wybrać plik
                    </p>
                    <p className="text-sm text-gray-500">
                      PDF, JPG, JPEG, PNG • Maksymalny rozmiar: 10MB
                    </p>
                  </label>
                </div>

                {selectedFile && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center">
                      {selectedFile.type === 'application/pdf' ? (
                        <svg className="w-8 h-8 text-red-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-8 h-8 text-green-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                        </svg>
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{selectedFile.name}</p>
                        <p className="text-sm text-gray-500">
                          {selectedFile.type === 'application/pdf' ? 'PDF' : 'Obraz'} • {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <h2 className="text-lg font-semibold mb-4">Tekst do Tłumaczenia</h2>
                
                <textarea
                  value={textToTranslate}
                  onChange={(e) => setTextToTranslate(e.target.value)}
                  placeholder="Wprowadź tekst techniczny do tłumaczenia..."
                  className="w-full h-64 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                
                <p className="text-sm text-gray-500 mt-2">
                  Długość: {textToTranslate.length} znaków
                </p>

                {translatedText && (
                  <div className="mt-6">
                    <h3 className="text-md font-semibold mb-2">Przetłumaczony Tekst:</h3>
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <pre className="whitespace-pre-wrap text-sm">{translatedText}</pre>
                    </div>
                    
                    <Button
                      variant="outline"
                      onClick={() => navigator.clipboard.writeText(translatedText)}
                      className="mt-3"
                    >
                      Skopiuj przetłumaczony tekst
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Info Section */}
      <Card className="mt-6">
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">Informacje o Serwisie</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Obsługiwane formaty</h3>
              <ul className="text-gray-600 space-y-1">
                <li>• PDF (do 10MB)</li>
                <li>• JPG, JPEG, PNG (do 10MB)</li>
                <li>• Tekst zwykły</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Obsługiwane języki</h3>
              <p className="text-gray-600">
                {Object.keys(supportedLanguages).length} języków dostępnych
              </p>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Funkcje</h3>
              <ul className="text-gray-600 space-y-1">
                <li>• Tłumaczenie techniczne</li>
                <li>• OCR dla obrazów</li>
                <li>• Zachowanie formatowania</li>
                <li>• Kontekst specjalistyczny</li>
              </ul>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default DocumentTranslation;
